// PlanningFlex.js
// Composant de planning avec layout flexbox entièrement adaptatif
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale/fr';
import '../styles/PlanningFlex.css';

// Hook personnalisé pour détecter la taille d'écran
function useScreenSize() {
  const [screenSize, setScreenSize] = useState({
    isMobile: false,
    isTablet: false,
    isDesktop: true
  });

  useEffect(() => {
    const checkScreenSize = () => {
      const width = window.innerWidth;
      setScreenSize({
        isMobile: width <= 600,
        isTablet: width > 600 && width <= 900,
        isDesktop: width > 900
      });
    };

    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  return screenSize;
}

// Composant multi-sélection pour le filtre de train
function TrainMultiSelect({ values, selected, onChange, placeholder }) {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = React.useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleValue = (val) => {
    if (selected.includes(val)) {
      onChange(selected.filter(v => v !== val));
    } else {
      onChange([...selected, val]);
    }
  };

  const displayText = selected.length === 0
    ? (placeholder || 'Tous les trains')
    : selected.join(', ');

  return (
    <div className="pf-multiselect" ref={containerRef}>
      <button
        type="button"
        className="pf-filter-input pf-multiselect-toggle"
        onClick={() => setIsOpen(!isOpen)}
        title={displayText}
      >
        <span className="pf-multiselect-text">{displayText}</span>
        <span className="pf-multiselect-arrow">{isOpen ? '▲' : '▼'}</span>
      </button>
      {isOpen && (
        <div className="pf-multiselect-dropdown">
          {values.length === 0 ? (
            <div className="pf-multiselect-empty">Aucun type disponible</div>
          ) : (
            values.map(val => (
              <label key={val} className="pf-multiselect-item">
                <input
                  type="checkbox"
                  checked={selected.includes(val)}
                  onChange={() => toggleValue(val)}
                />
                <span>{val}</span>
              </label>
            ))
          )}
        </div>
      )}
    </div>
  );
}

// Composant pour le dropdown avec recherche
function SearchDropdown({ value, onChange, onSelect, placeholder, options, show, onShow, onHide }) {  return (
    <div className="pf-add-dropdown">
      <input
        type="text"
        className="pf-add-input"
        value={value}
        onChange={(e) => {
          onChange(e.target.value);
          onShow();
        }}
        onFocus={onShow}
        onBlur={() => setTimeout(onHide, 200)}
        placeholder={placeholder}
        autoComplete="off"
      />
      {show && options.length > 0 && (
        <ul className="pf-dropdown-list">
          {options.map((option) => (
            <li
              key={option.id}
              className="pf-dropdown-item"
              onMouseDown={() => {
                onSelect(option.nom_utilisateur);
                onHide();
              }}
            >
              {option.nom_utilisateur}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// Composant Modal pour ajout sur mobile
function AddModal({ isOpen, onClose, formData, setFormData, onSubmit, utilisateurs, utilisateursAvecCle, error, setError }) {
  const [dropdowns, setDropdowns] = useState({
    benecle: false, bene1: false, bene2: false, bene3: false, bene4: false, bene5: false, bene6: false
  });

  if (!isOpen) return null;

  const filterUsers = (users, searchTerm) => {
    if (!searchTerm) return users;
    return users.filter(u => u.nom_utilisateur.toLowerCase().includes(searchTerm.toLowerCase()));
  };

  return (
    <div className="pf-modal-overlay" onClick={onClose}>
      <div className="pf-modal" onClick={(e) => e.stopPropagation()}>
        <div className="pf-modal-header">
          <h3 className="pf-modal-title">Ajouter une entrée</h3>
          <button className="pf-modal-close" onClick={onClose}>&times;</button>
        </div>
        {error && (
          <div className="pf-error-message">
            {error}
          </div>
        )}
        <div className="pf-modal-body">
          <div className="pf-form-group">
            <label className="pf-form-label">Date *</label>
            <input
              type="date"
              className="pf-form-input"
              value={formData.date}
              onChange={(e) => setFormData({ ...formData, date: e.target.value })}
              required
            />
          </div>
          <div className="pf-form-group">
            <label className="pf-form-label">Horaire</label>
            <input
              type="text"
              className="pf-form-input"
              placeholder="Ex: 14h-18h"
              value={formData.horaire}
              onChange={(e) => setFormData({ ...formData, horaire: e.target.value })}
            />
          </div>
          <div className="pf-form-group">
            <label className="pf-form-label">Train</label>
            <input
              type="text"
              className="pf-form-input"
              placeholder="Type de train"
              value={formData.train}
              onChange={(e) => setFormData({ ...formData, train: e.target.value })}
            />
          </div>
          <div className="pf-form-group">
            <label className="pf-form-label">Bénévole clé</label>
            <SearchDropdown
              value={formData.benecle}
              onChange={(val) => setFormData({ ...formData, benecle: val })}
              onSelect={(val) => setFormData({ ...formData, benecle: val })}
              placeholder="Rechercher..."
              options={filterUsers(utilisateursAvecCle, formData.benecle)}
              show={dropdowns.benecle}
              onShow={() => setDropdowns({ ...dropdowns, benecle: true })}
              onHide={() => setDropdowns({ ...dropdowns, benecle: false })}
            />
          </div>
          {[1, 2, 3, 4, 5, 6].map((num) => (
            <div key={num} className="pf-form-group">
              <label className="pf-form-label">Bénévole {num}</label>
              <SearchDropdown
                value={formData[`bene${num}`]}
                onChange={(val) => setFormData({ ...formData, [`bene${num}`]: val })}
                onSelect={(val) => setFormData({ ...formData, [`bene${num}`]: val })}
                placeholder="Rechercher..."
                options={filterUsers(utilisateurs, formData[`bene${num}`])}
                show={dropdowns[`bene${num}`]}
                onShow={() => setDropdowns({ ...dropdowns, [`bene${num}`]: true })}
                onHide={() => setDropdowns({ ...dropdowns, [`bene${num}`]: false })}
              />
            </div>
          ))}
          <div className="pf-form-group">
            <label className="pf-form-label">Commentaire</label>
            <input
              type="text"
              className="pf-form-input"
              placeholder="Commentaire optionnel"
              value={formData.comment}
              onChange={(e) => setFormData({ ...formData, comment: e.target.value })}
            />
          </div>
        </div>
        <div className="pf-modal-footer">
          <button className="pf-btn pf-btn-secondary" onClick={onClose}>Annuler</button>
          <button className="pf-btn pf-btn-primary" onClick={onSubmit}>Ajouter</button>
        </div>
      </div>
    </div>
  );
}

// Composant pour une ligne du planning (mode desktop)
function PlanningRowDesktop({ item, isEditing, editFormData, setEditFormData, onEdit, onSave, onCancel, onDelete, onToggleCancel, utilisateurs, utilisateursAvecCle, droit, isAdmin }) {
  const getSortedUsers = (users, selectedValue) => {
    if (!selectedValue) return users;
    const selected = users.find(user => user.nom_utilisateur === selectedValue);
    if (!selected) return users;
    return [selected, ...users.filter(user => user.nom_utilisateur !== selectedValue)];
  };

  if (isEditing) {
    return (
      <div className="pf-row pf-edit-row">
        <div className="pf-cell pf-cell-date">
          <input
            type="date"
            className="pf-edit-input"
            value={editFormData.date}
            onChange={(e) => setEditFormData({ ...editFormData, date: e.target.value })}
          />
        </div>
        <div className="pf-cell pf-cell-horaire">
          <input
            type="text"
            className="pf-edit-input"
            value={editFormData.horaire}
            onChange={(e) => setEditFormData({ ...editFormData, horaire: e.target.value })}
          />
        </div>
        <div className="pf-cell pf-cell-train">
          <input
            type="text"
            className="pf-edit-input"
            value={editFormData.train}
            onChange={(e) => setEditFormData({ ...editFormData, train: e.target.value })}
          />
        </div>
        <div className="pf-cell pf-cell-benevole">
          <select
            className="pf-edit-select"
            value={editFormData.benecle}
            onChange={(e) => setEditFormData({ ...editFormData, benecle: e.target.value })}
          >
            <option value="">Sélectionner</option>
            {getSortedUsers(utilisateursAvecCle, editFormData.benecle).map((user) => (
              <option key={user.id} value={user.nom_utilisateur}>{user.nom_utilisateur}</option>
            ))}
          </select>
        </div>
        {[1, 2, 3, 4, 5, 6].map((num) => (
          <div key={num} className="pf-cell pf-cell-benevole">
            <select
              className="pf-edit-select"
              value={editFormData[`bene${num}`]}
              onChange={(e) => setEditFormData({ ...editFormData, [`bene${num}`]: e.target.value })}
            >
              <option value="">Sélectionner</option>
              {getSortedUsers(utilisateurs, editFormData[`bene${num}`]).map((user) => (
                <option key={user.id} value={user.nom_utilisateur}>{user.nom_utilisateur}</option>
              ))}
            </select>
          </div>
        ))}
        <div className="pf-cell pf-cell-comment">
          <input
            type="text"
            className="pf-edit-input"
            value={editFormData.comment}
            onChange={(e) => setEditFormData({ ...editFormData, comment: e.target.value })}
          />
        </div>
        <div className="pf-cell pf-cell-actions">
          <div className="pf-actions">
            <button className="pf-btn pf-btn-secondary pf-btn-sm" onClick={onSave}>Enregistrer</button>
            <button className="pf-btn pf-btn-danger pf-btn-sm" onClick={onCancel}>Annuler</button>
          </div>
        </div>
      </div>
    );
  }

  const isMissing = (val) => val === 'x ?';

  return (
    <div className={`pf-row ${item.is_cancelled ? 'pf-row-cancelled' : ''}`}>
      <div className="pf-cell pf-cell-date">{format(new Date(item.date), 'EEE d MMM', { locale: fr })}</div>
      <div className="pf-cell pf-cell-horaire">{item.horaire}</div>
      <div className={`pf-cell pf-cell-train ${item.train && item.train.toLowerCase() === 'gare' ? 'pf-cell-train-gare' : ''}`}>{item.train}</div>
      {droit !== 'muser' && (
        <>
          <div className={`pf-cell pf-cell-benevole ${isMissing(item.benecle) ? 'pf-missing' : ''}`}>{item.benecle}</div>
          <div className={`pf-cell pf-cell-benevole ${isMissing(item.bene1) ? 'pf-missing' : ''}`}>{item.bene1}</div>
          <div className={`pf-cell pf-cell-benevole ${isMissing(item.bene2) ? 'pf-missing' : ''}`}>{item.bene2}</div>
          <div className={`pf-cell pf-cell-benevole ${isMissing(item.bene3) ? 'pf-missing' : ''}`}>{item.bene3}</div>
          <div className={`pf-cell pf-cell-benevole ${isMissing(item.bene4) ? 'pf-missing' : ''}`}>{item.bene4}</div>
          <div className={`pf-cell pf-cell-benevole ${isMissing(item.bene5) ? 'pf-missing' : ''}`}>{item.bene5}</div>
          <div className={`pf-cell pf-cell-benevole ${isMissing(item.bene6) ? 'pf-missing' : ''}`}>{item.bene6}</div>
          <div className="pf-cell pf-cell-comment">{item.comment}</div>
        </>
      )}
      {isAdmin && droit !== 'muser' && (
        <div className="pf-cell pf-cell-actions">
          <div className="pf-actions">
            <button className="pf-btn pf-btn-secondary pf-btn-sm" onClick={onEdit}>Modifier</button>
            <button className="pf-btn pf-btn-danger pf-btn-sm" onClick={onDelete}>Supprimer</button>
            <button
              className={`pf-btn pf-btn-sm ${item.is_cancelled ? 'pf-btn-secondary' : 'pf-btn-warning'}`}
              onClick={onToggleCancel}
            >
              {item.is_cancelled ? 'Planifier' : 'Annuler'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// Composant pour une ligne du planning (mode mobile)
function PlanningRowMobile({ item, isAdmin, droit, onEdit, onDelete, onToggleCancel }) {
  const isMissing = (val) => val === 'x ?';

  return (
    <div className={`pf-row ${item.is_cancelled ? 'pf-row-cancelled' : ''}`}>
      <div className="pf-cell-date-time-train-mobile">
        <span className="pf-date-mobile">
          {format(new Date(item.date), 'EEE d MMM', { locale: fr })}
        </span>
        <span className="pf-horaire-mobile">{item.horaire || '-'}</span>
        <span className={`pf-train-mobile ${item.train && item.train.toLowerCase() === 'gare' ? 'pf-train-mobile-gare' : ''}`}>{item.train || '-'}</span>
      </div>
      
      {droit !== 'muser' && (
        <div className="pf-cell-benevoles-mobile">
          {/* Bénévole Clé */}
          {item.benecle && (
            <div className={`pf-benevole-item pf-benevole-cle ${isMissing(item.benecle) ? 'pf-missing' : ''}`}>
              <span className="pf-benevole-label">🔑</span>
              <span>{item.benecle}</span>
            </div>
          )}
          
          {/* Bénévoles 1, 2, 3 sur une même ligne */}
          {(item.bene1 || item.bene2 || item.bene3) && (
            <div className="pf-benevoles-row">
              {item.bene1 && <span className={`pf-benevole-inline ${isMissing(item.bene1) ? 'pf-missing' : ''}`}>{item.bene1}</span>}
              {item.bene2 && <span className={`pf-benevole-inline ${isMissing(item.bene2) ? 'pf-missing' : ''}`}>{item.bene2}</span>}
              {item.bene3 && <span className={`pf-benevole-inline ${isMissing(item.bene3) ? 'pf-missing' : ''}`}>{item.bene3}</span>}
            </div>
          )}
          
          {/* Bénévoles 4, 5, 6 sur une même ligne */}
          {(item.bene4 || item.bene5 || item.bene6) && (
            <div className="pf-benevoles-row">
              {item.bene4 && <span className={`pf-benevole-inline ${isMissing(item.bene4) ? 'pf-missing' : ''}`}>{item.bene4}</span>}
              {item.bene5 && <span className={`pf-benevole-inline ${isMissing(item.bene5) ? 'pf-missing' : ''}`}>{item.bene5}</span>}
              {item.bene6 && <span className={`pf-benevole-inline ${isMissing(item.bene6) ? 'pf-missing' : ''}`}>{item.bene6}</span>}
            </div>
          )}
        </div>
      )}

      {item.comment && (
        <div className="pf-cell-comment-mobile">
          💬 {item.comment}
        </div>
      )}

      {isAdmin && droit !== 'muser' && (
        <div className="pf-cell pf-cell-actions">
          <div className="pf-actions">
            <button className="pf-btn pf-btn-secondary pf-btn-sm" onClick={onEdit}>Modifier</button>
            <button className="pf-btn pf-btn-danger pf-btn-sm" onClick={onDelete}>Supprimer</button>
            <button
              className={`pf-btn pf-btn-sm ${item.is_cancelled ? 'pf-btn-secondary' : 'pf-btn-warning'}`}
              onClick={onToggleCancel}
            >
              {item.is_cancelled ? 'Planifier' : 'Annuler'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// Composant principal PlanningFlex
function PlanningFlex({ token, droit }) {
  const [planning, setPlanning] = useState([]);
  const [utilisateurs, setUtilisateurs] = useState([]);
  const [utilisateursAvecCle, setUtilisateursAvecCle] = useState([]);
  const [formData, setFormData] = useState({
    date: '', horaire: '', train: '', benecle: '', bene1: '', bene2: '', bene3: '', bene4: '', bene5: '', bene6: '', comment: ''
  });
  const [editingId, setEditingId] = useState(null);
  const [editFormData, setEditFormData] = useState({
    date: '', horaire: '', train: '', benecle: '', bene1: '', bene2: '', bene3: '', bene4: '', bene5: '', bene6: '', comment: ''
  });
  const [sortConfig, setSortConfig] = useState({ key: 'date', direction: 'ascending' });
  const [showAddModal, setShowAddModal] = useState(false);
  const [error, setError] = useState('');
  const { isMobile } = useScreenSize();

  const [filters, setFilters] = useState({ date: '', nom: '' });
  const [selectedTrains, setSelectedTrains] = useState([]);
  const [trainValues, setTrainValues] = useState([]);
  const [monthFilter, setMonthFilter] = useState('');
  const [shouldApplyMonthFilter, setShouldApplyMonthFilter] = useState(false);
  const [includeEmptyHoraire, setIncludeEmptyHoraire] = useState(false);
  const [showNomDropdown, setShowNomDropdown] = useState(false);
  const [filteredUtilisateurs, setFilteredUtilisateurs] = useState([]);

  // États pour les dropdowns d'ajout
  const [dropdowns, setDropdowns] = useState({
    benecle: false, bene1: false, bene2: false, bene3: false, bene4: false, bene5: false, bene6: false
  });

  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
  const isAdmin = droit === 'admin' || droit === 'sadmin';

  // Récupérer le planning
  const fetchPlanning = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (filters.date) params.append('date', filters.date);
      if (selectedTrains.length > 0) params.append('trains', selectedTrains.join(','));
      if (droit !== 'muser' && filters.nom) params.append('nom', filters.nom);
      
      const endpoint = droit === 'muser' ? '/api/planning/muser' : '/api/planning';
      const url = `${API_URL}${endpoint}${params.toString() ? `?${params.toString()}` : ''}`;

      const response = await axios.get(url, {
        headers: { Authorization: token },
      });

      setPlanning(response.data || []);
    } catch (error) {
      console.error("Erreur lors de la récupération du planning :", error);
    }
  }, [token, filters, selectedTrains, droit, API_URL]);

  // Récupérer les utilisateurs
  const fetchUtilisateurs = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/utilisateurs`, {
        headers: { Authorization: token },
      });
      setUtilisateurs(response.data);
    } catch (error) {
      console.error(error);
    }
  }, [token, API_URL]);

  // Récupérer les utilisateurs avec clé
  const fetchUtilisateursAvecCle = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/utilisateursaveccle`, {
        headers: { Authorization: token },
      });
      setUtilisateursAvecCle(response.data);
    } catch (error) {
      console.error(error);
    }
  }, [token, API_URL]);

  // Récupérer les valeurs distinctes de train
  const fetchTrainValues = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/planning/trains`, {
        headers: { Authorization: token },
      });
      setTrainValues(response.data || []);
    } catch (error) {
      console.error('Erreur lors de la récupération des types de train :', error);
    }
  }, [token, API_URL]);

  // Ajouter une ligne
  const handleAddPlanning = async () => {
    // Validation de la longueur du commentaire
    if (formData.comment.length > 39) {
      setError('Le commentaire ne doit pas dépasser 39 caractères.');
      return;
    }
    
    try {
      setError('');
      await axios.post(`${API_URL}/api/planning`, formData, {
        headers: { Authorization: token },
      });
      fetchPlanning();
      setFormData({
        date: '', horaire: '', train: '', benecle: '', bene1: '', bene2: '', bene3: '', bene4: '', bene5: '', bene6: '', comment: ''
      });
      setShowAddModal(false);
    } catch (error) {
      console.error(error);
      setError('Erreur lors de l\'ajout : ' + (error.response?.data || error.message));
    }
  };

  // Modifier une ligne
  const handleEditPlanning = async () => {
    // Validation de la longueur du commentaire
    if (editFormData.comment.length > 39) {
      setError('Le commentaire ne doit pas dépasser 39 caractères.');
      return;
    }
    
    try {
      setError('');
      await axios.put(`${API_URL}/api/planning/${editingId}`, editFormData, {
        headers: { Authorization: token },
      });
      fetchPlanning();
      setEditingId(null);
    } catch (error) {
      console.error('Erreur lors de la modification :', error.response?.data || error.message);
      setError('Erreur lors de la modification : ' + (error.response?.data || error.message));
    }
  };

  // Supprimer une ligne
  const handleDeletePlanning = async (id) => {
    if (window.confirm("Voulez-vous vraiment supprimer cette ligne ?")) {
      try {
        await axios.delete(`${API_URL}/api/planning/${id}`, {
          headers: { Authorization: token },
        });
        fetchPlanning();
      } catch (error) {
        console.error('Erreur lors de la suppression :', error.response?.data || error.message);
        alert('Erreur lors de la suppression : ' + (error.response?.data || error.message));
      }
    }
  };

  // Basculer annulation
  const handleToggleCancelPlanning = async (id, isCancelled) => {
    const newState = !isCancelled;
    const confirmMessage = newState ? 'Voulez-vous annuler cette ligne ?' : 'Voulez-vous planifier cette ligne ?';
    
    if (window.confirm(confirmMessage)) {
      try {
        await axios.put(`${API_URL}/api/planning/${id}/cancel`, 
          { cancelled: newState }, 
          { headers: { Authorization: token } }
        );
        fetchPlanning();
      } catch (error) {
        console.error(`Erreur :`, error.response?.data || error.message);
        alert(`Erreur : ` + (error.response?.data || error.message));
      }
    }
  };

  // Trier
  const requestSort = (key) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };

  const sortedPlanning = React.useMemo(() => {
    let sortablePlanning = [...planning];
    
    // Filtrer les lignes avec horaire vide si includeEmptyHoraire est false
    if (!includeEmptyHoraire) {
      sortablePlanning = sortablePlanning.filter(item => {
        const hasHoraire = item.horaire && item.horaire.trim() !== '';
        return hasHoraire;
      });
    }

    // Filtrer par mois si le filtre est activé et monthFilter est sélectionné
    if (shouldApplyMonthFilter && monthFilter) {
      const [selectedYear, selectedMonth] = monthFilter.split('-');
      sortablePlanning = sortablePlanning.filter(item => {
        const itemDate = new Date(item.date);
        const itemYear = itemDate.getFullYear().toString();
        const itemMonth = String(itemDate.getMonth() + 1).padStart(2, '0');
        return itemYear === selectedYear && itemMonth === selectedMonth;
      });
    }
    
    if (sortConfig.key) {
      sortablePlanning.sort((a, b) => {
        if (new Date(a[sortConfig.key]) < new Date(b[sortConfig.key])) {
          return sortConfig.direction === 'ascending' ? -1 : 1;
        }
        if (new Date(a[sortConfig.key]) > new Date(b[sortConfig.key])) {
          return sortConfig.direction === 'ascending' ? 1 : -1;
        }
        return 0;
      });
    }
    return sortablePlanning;
  }, [planning, sortConfig, includeEmptyHoraire, monthFilter, shouldApplyMonthFilter]);

  // Vérifier changement de mois
  const isNewMonth = (currentItem, previousItem) => {
    if (!previousItem) return true; // Afficher le mois pour la première ligne
    const currentDate = new Date(currentItem.date);
    const previousDate = new Date(previousItem.date);
    return currentDate.getMonth() !== previousDate.getMonth() || 
           currentDate.getFullYear() !== previousDate.getFullYear();
  };

  // Filtrer les utilisateurs
  const filterUsers = (users, searchTerm) => {
    if (!searchTerm) return users;
    return users.filter(u => u.nom_utilisateur.toLowerCase().includes(searchTerm.toLowerCase()));
  };

  // Extraire les mois uniques du planning
  const getAvailableMonths = () => {
    const months = new Set();
    planning.forEach(item => {
      const date = new Date(item.date);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      months.add(`${year}-${month}`);
    });
    return Array.from(months).sort().reverse(); // Les mois les plus récents d'abord
  };

  const formatMonthDisplay = (monthString) => {
    const [year, month] = monthString.split('-');
    const date = new Date(year, parseInt(month) - 1, 1);
    return format(date, 'LLLL yyyy', { locale: fr }).charAt(0).toUpperCase() + format(date, 'LLLL yyyy', { locale: fr }).slice(1);
  };

  // Initialiser le filtre mois avec le mois en cours au montage
  useEffect(() => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    setMonthFilter(`${year}-${month}`);
  }, []);

  // Effects
  useEffect(() => {
    if (token) {
      fetchPlanning();
      fetchTrainValues();
      if (isAdmin) {
        fetchUtilisateurs();
        fetchUtilisateursAvecCle();
      } else if (droit !== 'muser') {
        fetchUtilisateurs();
      }
    }
  }, [token, droit, fetchPlanning, fetchUtilisateurs, fetchUtilisateursAvecCle, fetchTrainValues, includeEmptyHoraire, isAdmin]);

  useEffect(() => {
    if (filters.nom === '') {
      setFilteredUtilisateurs(utilisateurs);
    } else {
      setFilteredUtilisateurs(utilisateurs.filter(user =>
        user.nom_utilisateur.toLowerCase().includes(filters.nom.toLowerCase())
      ));
    }
  }, [filters.nom, utilisateurs]);

  return (
    <div className={`pf-container ${droit === 'muser' ? 'pf-container-muser' : ''}`}>
      <h1 className="pf-title">Planning</h1>
      
      {/* Filtres */}
      <div className="pf-filters">
        <div className="pf-filter-group">
          <select
            className="pf-filter-input"
            value={monthFilter}
            onChange={(e) => setMonthFilter(e.target.value)}
          >
            {getAvailableMonths().map((monthString) => (
              <option key={monthString} value={monthString}>
                {formatMonthDisplay(monthString)}
              </option>
            ))}
          </select>
        </div>
        <div className="pf-filter-group">
          <input
            type="date"
            className="pf-filter-input"
            value={filters.date}
            onChange={(e) => setFilters({ ...filters, date: e.target.value })}
          />
        </div>
        <div className="pf-filter-group">
          <TrainMultiSelect
            values={trainValues}
            selected={selectedTrains}
            onChange={setSelectedTrains}
            placeholder="Tous les trains"
          />
        </div>
        {droit !== 'muser' && (
          <div className="pf-filter-group">
            <input
              type="text"
              className="pf-filter-input"
              placeholder="Nom du bénévole"
              value={filters.nom}
              onChange={(e) => {
                setFilters({ ...filters, nom: e.target.value });
                setShowNomDropdown(true);
              }}
              onFocus={() => setShowNomDropdown(true)}
              onBlur={() => setTimeout(() => setShowNomDropdown(false), 200)}
              autoComplete="off"
            />
            {showNomDropdown && filteredUtilisateurs.length > 0 && (
              <ul className="pf-dropdown-list">
                {filteredUtilisateurs.map((user) => (
                  <li
                    key={user.id}
                    className="pf-dropdown-item"
                    onMouseDown={() => {
                      setFilters({ ...filters, nom: user.nom_utilisateur });
                      setShowNomDropdown(false);
                    }}
                  >
                    {user.nom_utilisateur}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
        <div className="pf-filter-actions">
          <button 
            onClick={() => {
              setShouldApplyMonthFilter(true);
              fetchPlanning();
            }} 
            className="pf-btn pf-btn-primary"
          >
            Filtrer
          </button>
          <button
            onClick={() => {
              const availableMonths = getAvailableMonths();
              if (availableMonths.length > 0) {
                setMonthFilter(availableMonths[0]); // Le mois le plus récent
              }
              setShouldApplyMonthFilter(false);
              setFilters({ date: '', nom: '' });
              setSelectedTrains([]);
              fetchPlanning();
            }}
            className="pf-btn pf-btn-secondary"
          >
            Réinitialiser
          </button>
        </div>
        {isAdmin && (
          <div className="pf-checkboxes-container">
 
            <label className="pf-checkbox-label">
              <input
                type="checkbox"
                checked={includeEmptyHoraire}
                onChange={(e) => {
                  setIncludeEmptyHoraire(e.target.checked);
                }}
              />
              Inclure les lignes sans horaire défini
            </label>
          </div>
        )}
      </div>

      {/* Grille du planning */}
      <div className="pf-grid-container">
        {/* En-tête (masqué sur mobile) */}
        {!isMobile && (
          <div className="pf-grid-header">
            <div className="pf-cell pf-cell-date" onClick={() => requestSort('date')}>
              Date {sortConfig.key === 'date' && (sortConfig.direction === 'ascending' ? '↑' : '↓')}
            </div>
            <div className="pf-cell pf-cell-horaire">Horaire</div>
            <div className="pf-cell pf-cell-train">Train</div>
            {droit !== 'muser' && (
              <>
                <div className="pf-cell pf-cell-benevole">Clé</div>
                <div className="pf-cell pf-cell-benevole">Bénévole 1</div>
                <div className="pf-cell pf-cell-benevole">Bénévole 2</div>
                <div className="pf-cell pf-cell-benevole">Bénévole 3</div>
                <div className="pf-cell pf-cell-benevole">Bénévole 4</div>
                <div className="pf-cell pf-cell-benevole">Bénévole 5</div>
                <div className="pf-cell pf-cell-benevole">Bénévole 6</div>
                <div className="pf-cell pf-cell-comment">Commentaire</div>
              </>
            )}
            {isAdmin && droit !== 'muser' && <div className="pf-cell pf-cell-actions">Actions</div>}
          </div>
        )}

        {/* Ligne d'ajout (desktop seulement) */}
        {!isMobile && isAdmin && (
          <div className="pf-add-row">
            <div className="pf-cell pf-cell-date">
              <input
                type="date"
                className="pf-add-input"
                value={formData.date}
                onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                required
              />
            </div>
            <div className="pf-cell pf-cell-horaire">
              <input
                type="text"
                className="pf-add-input"
                placeholder="Horaire"
                value={formData.horaire}
                onChange={(e) => setFormData({ ...formData, horaire: e.target.value })}
              />
            </div>
            <div className="pf-cell pf-cell-train">
              <input
                type="text"
                className="pf-add-input"
                placeholder="Train"
                value={formData.train}
                onChange={(e) => setFormData({ ...formData, train: e.target.value })}
              />
            </div>
            <div className="pf-cell pf-cell-benevole">
              <SearchDropdown
                value={formData.benecle}
                onChange={(val) => setFormData({ ...formData, benecle: val })}
                onSelect={(val) => setFormData({ ...formData, benecle: val })}
                placeholder="Clé"
                options={filterUsers(utilisateursAvecCle, formData.benecle)}
                show={dropdowns.benecle}
                onShow={() => setDropdowns({ ...dropdowns, benecle: true })}
                onHide={() => setDropdowns({ ...dropdowns, benecle: false })}
              />
            </div>
            {[1, 2, 3, 4, 5, 6].map((num) => (
              <div key={num} className="pf-cell pf-cell-benevole">
                <SearchDropdown
                  value={formData[`bene${num}`]}
                  onChange={(val) => setFormData({ ...formData, [`bene${num}`]: val })}
                  onSelect={(val) => setFormData({ ...formData, [`bene${num}`]: val })}
                  placeholder={`Bén. ${num}`}
                  options={filterUsers(utilisateurs, formData[`bene${num}`])}
                  show={dropdowns[`bene${num}`]}
                  onShow={() => setDropdowns({ ...dropdowns, [`bene${num}`]: true })}
                  onHide={() => setDropdowns({ ...dropdowns, [`bene${num}`]: false })}
                />
              </div>
            ))}
            <div className="pf-cell pf-cell-comment">
              <input
                type="text"
                className="pf-add-input"
                placeholder="Commentaire"
                value={formData.comment}
                onChange={(e) => setFormData({ ...formData, comment: e.target.value })}
              />
            </div>
            <div className="pf-cell pf-cell-actions">
              <button className="pf-btn pf-btn-secondary" onClick={handleAddPlanning}>Ajouter</button>
            </div>
          </div>
        )}

        {/* Corps */}
        <div className="pf-grid-body">
          {sortedPlanning.map((item, index) => (
            <React.Fragment key={item.id}>
              {isNewMonth(item, sortedPlanning[index - 1]) && (
                <div className="pf-row pf-month-separator">
                  {format(new Date(item.date), 'LLLL yyyy', { locale: fr })}
                </div>
              )}
              {isMobile ? (
                <PlanningRowMobile
                  item={item}
                  isAdmin={isAdmin}
                  droit={droit}
                  onEdit={() => {
                    setEditingId(item.id);
                    setEditFormData({
                      date: format(new Date(item.date), 'yyyy-MM-dd'),
                      horaire: item.horaire,
                      train: item.train,
                      benecle: item.benecle,
                      bene1: item.bene1,
                      bene2: item.bene2,
                      bene3: item.bene3,
                      bene4: item.bene4,
                      bene5: item.bene5,
                      bene6: item.bene6,
                      comment: item.comment,
                    });
                    setShowAddModal(true);
                  }}
                  onDelete={() => handleDeletePlanning(item.id)}
                  onToggleCancel={() => handleToggleCancelPlanning(item.id, item.is_cancelled)}
                />
              ) : (
                <PlanningRowDesktop
                  item={item}
                  isEditing={editingId === item.id}
                  editFormData={editFormData}
                  setEditFormData={setEditFormData}
                  onEdit={() => {
                    setEditingId(item.id);
                    setEditFormData({
                      date: format(new Date(item.date), 'yyyy-MM-dd'),
                      horaire: item.horaire,
                      train: item.train,
                      benecle: item.benecle,
                      bene1: item.bene1,
                      bene2: item.bene2,
                      bene3: item.bene3,
                      bene4: item.bene4,
                      bene5: item.bene5,
                      bene6: item.bene6,
                      comment: item.comment,
                    });
                  }}
                  onSave={handleEditPlanning}
                  onCancel={() => setEditingId(null)}
                  onDelete={() => handleDeletePlanning(item.id)}
                  onToggleCancel={() => handleToggleCancelPlanning(item.id, item.is_cancelled)}
                  utilisateurs={utilisateurs}
                  utilisateursAvecCle={utilisateursAvecCle}
                  droit={droit}
                  isAdmin={isAdmin}
                />
              )}
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* Bouton flottant d'ajout (mobile) */}
      {isAdmin && (
        <button className="pf-fab-add" onClick={() => setShowAddModal(true)}>+</button>
      )}

      {/* Modal d'ajout (mobile) */}
      <AddModal
        isOpen={showAddModal}
        onClose={() => {
          setShowAddModal(false);
          setEditingId(null);
          setError('');
        }}
        formData={editingId ? editFormData : formData}
        setFormData={editingId ? setEditFormData : setFormData}
        onSubmit={editingId ? handleEditPlanning : handleAddPlanning}
        utilisateurs={utilisateurs}
        utilisateursAvecCle={utilisateursAvecCle}
        error={error}
        setError={setError}
      />
    </div>
  );
}

export default PlanningFlex;
